import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, ArrowRight, X, Bookmark, Smartphone, Monitor, Award,
  Download, FileText, Presentation, CheckCircle2, Sparkles, ChevronRight,
} from 'lucide-react';
import type { AdminRealm } from '@/lib/adminAuth';
import type { TrainingProgress } from '@/data/trainingTypes';
import {
  PRESENTATION_SLIDES, PRESENTATION_STEPS, REVIEW_INDEX, REVIEW_BIG_IDEAS,
} from '@/data/trainingPresentation';
import {
  addTimeSpent, markSectionComplete, markSlideVisited, setLastPosition, toggleBookmark,
} from '@/lib/trainingProgress';
import { downloadTrainingCopyPdf, downloadInvestorDeckPdf } from '@/lib/trainingPresentationPdf';

interface Props {
  realm: AdminRealm;
  progress: TrainingProgress;
  onProgress: (p: TrainingProgress) => void;
  startIndex: number;
  onExit: () => void;
  onStartQuiz: () => void;
}

// ── Device frames ────────────────────────────────────────────────────────────

const PhoneFrame: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <div className="relative mx-auto w-[230px] sm:w-[260px]">
    <div className="relative overflow-hidden rounded-[2.6rem] border-[6px] border-[#1c2333] bg-black shadow-2xl shadow-black/60 ring-1 ring-white/10">
      {/* Dynamic island */}
      <div className="absolute left-1/2 top-2 z-10 h-[22px] w-[86px] -translate-x-1/2 rounded-full bg-black ring-1 ring-white/10" />
      <img src={src} alt={alt} className="aspect-[9/16] w-full object-cover" loading="lazy" />
      {/* Home indicator */}
      <div className="absolute bottom-1.5 left-1/2 z-10 h-1 w-24 -translate-x-1/2 rounded-full bg-white/60" />
    </div>
    {/* Side buttons */}
    <div className="absolute -left-[3px] top-20 h-8 w-[3px] rounded-l bg-[#2a3145]" />
    <div className="absolute -left-[3px] top-32 h-12 w-[3px] rounded-l bg-[#2a3145]" />
    <div className="absolute -right-[3px] top-24 h-14 w-[3px] rounded-r bg-[#2a3145]" />
  </div>
);

const DesktopFrame: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <div className="mx-auto w-full max-w-xl overflow-hidden rounded-xl border border-white/15 bg-[#141a2a] shadow-2xl shadow-black/50">
    <div className="flex items-center gap-2 border-b border-white/10 bg-[#1a2136] px-4 py-2.5">
      <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
      <div className="ml-3 flex-1 truncate rounded-md bg-white/[0.06] px-3 py-1 text-[10px] text-white/40">
        socialmediahub.app — Social Media Hub
      </div>
    </div>
    <img src={src} alt={alt} className="aspect-video w-full object-cover" loading="lazy" />
  </div>
);

// ── Player ───────────────────────────────────────────────────────────────────

const PresentationPlayer: React.FC<Props> = ({ realm, progress, onProgress, startIndex, onExit, onStartQuiz }) => {
  const [idx, setIdx] = useState(() => Math.min(Math.max(startIndex, 0), REVIEW_INDEX));
  const [device, setDevice] = useState<'mobile' | 'desktop'>('mobile');
  const [showDownloads, setShowDownloads] = useState(false);
  const enteredAt = useRef(Date.now());

  const isReview = idx === REVIEW_INDEX;
  const slide = isReview ? null : PRESENTATION_SLIDES[idx];
  const slideId = isReview ? 'pres-review' : PRESENTATION_SLIDES[idx].id;

  // Track visits, section completion + last position
  useEffect(() => {
    let p = markSlideVisited(realm, slideId);
    p = setLastPosition(realm, 0, idx);
    if (!isReview) {
      PRESENTATION_SLIDES[idx].sectionIds.forEach((sid) => {
        p = markSectionComplete(realm, sid);
      });
    }
    onProgress(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // Time tracking on unmount
  useEffect(() => {
    const t = enteredAt.current;
    return () => {
      onProgress(addTimeSpent(realm, Math.round((Date.now() - t) / 1000)));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goNext = useCallback(() => setIdx((i) => Math.min(i + 1, REVIEW_INDEX)), []);
  const goPrev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') onExit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, onExit]);

  const bookmarked = progress.bookmarks.includes(slideId);
  const pct = Math.round(((idx + 1) / PRESENTATION_STEPS) * 100);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 lg:px-8">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={onExit}
          className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-white/70 hover:border-[#C9A961]/50 hover:text-white"
        >
          <X className="h-3.5 w-3.5" /> Exit to Academy
        </button>
        <div className="hidden text-[11px] uppercase tracking-[0.3em] text-white/40 sm:block">
          Social Media Hub Mastery
        </div>
        <div className="flex items-center gap-2">
          {/* Downloads */}
          <div className="relative">
            <button
              onClick={() => setShowDownloads((v) => !v)}
              className="flex items-center gap-2 rounded-full border border-[#C9A961]/40 bg-[#C9A961]/10 px-4 py-2 text-xs text-[#E4C77F] hover:bg-[#C9A961]/20"
            >
              <Download className="h-3.5 w-3.5" /> Download slideshow
            </button>
            {showDownloads && (
              <div className="absolute right-0 top-11 z-30 w-72 rounded-2xl border border-white/15 bg-[#0d1526] p-2 shadow-2xl shadow-black/60">
                <button
                  onClick={() => { setShowDownloads(false); downloadTrainingCopyPdf(); }}
                  className="flex w-full items-start gap-3 rounded-xl p-3 text-left hover:bg-white/[0.05]"
                >
                  <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#C9A961]" />
                  <span>
                    <span className="block text-xs font-medium text-white">Training copy (PDF)</span>
                    <span className="block text-[10px] leading-relaxed text-white/45">One-for-one slides + all assessment questions with answers</span>
                  </span>
                </button>
                <button
                  onClick={() => { setShowDownloads(false); downloadInvestorDeckPdf(); }}
                  className="flex w-full items-start gap-3 rounded-xl p-3 text-left hover:bg-white/[0.05]"
                >
                  <Presentation className="mt-0.5 h-4 w-4 shrink-0 text-[#C9A961]" />
                  <span>
                    <span className="block text-xs font-medium text-white">Investor & client deck (PDF)</span>
                    <span className="block text-[10px] leading-relaxed text-white/45">The same slides reframed to pitch the platform's features</span>
                  </span>
                </button>
                <p className="px-3 pb-1 pt-2 text-[9px] text-white/30">Opens a print view — choose "Save as PDF" as the destination.</p>
              </div>
            )}
          </div>
          <button
            onClick={() => onProgress(toggleBookmark(realm, slideId))}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs transition-colors ${
              bookmarked
                ? 'border-[#C9A961]/60 bg-[#C9A961]/15 text-[#E4C77F]'
                : 'border-white/15 text-white/60 hover:border-[#C9A961]/50 hover:text-white'
            }`}
          >
            <Bookmark className={`h-3.5 w-3.5 ${bookmarked ? 'fill-[#E4C77F]' : ''}`} />
            {bookmarked ? 'Bookmarked' : 'Bookmark'}
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between text-[11px] text-white/45">
          <span>Slide {idx + 1} of {PRESENTATION_STEPS}{isReview ? ' · Course review' : ''}</span>
          <span>{pct}%</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-white/10">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#C9A961] to-[#E4C77F] transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* ── Slide body ── */}
      {slide ? (
        <div className="mt-6 grid gap-8 rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-10 lg:grid-cols-[1.1fr_1fr]">
          {/* Text column */}
          <div>
            <div className="text-[11px] font-medium uppercase tracking-[0.35em] text-[#C9A961]">{slide.kicker}</div>
            <h1 className="mt-3 text-2xl font-light tracking-tight text-white sm:text-4xl">{slide.title}</h1>
            <p className="mt-4 text-sm leading-relaxed text-white/55">{slide.lead}</p>
            <div className="mt-6 space-y-3">
              {slide.points.map((p, i) => (
                <div key={p.title} className="flex gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[#C9A961]/40 bg-[#C9A961]/10 text-xs font-semibold text-[#E4C77F]">
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">{p.title}</div>
                    <div className="mt-0.5 text-xs leading-relaxed text-white/50">{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            {slide.tip && (
              <div className="mt-5 rounded-2xl border border-[#C9A961]/30 bg-[#C9A961]/[0.08] p-4">
                <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-[#E4C77F]">
                  <Sparkles className="h-3.5 w-3.5" /> {slide.tip.title}
                </div>
                <p className="mt-2 text-xs leading-relaxed text-white/60">{slide.tip.text}</p>
              </div>
            )}
          </div>

          {/* Screenshot column */}
          <div className="flex flex-col">
            <div className="mb-4 flex items-center justify-center gap-1 self-center rounded-full border border-white/15 bg-white/[0.03] p-1">
              <button
                onClick={() => setDevice('mobile')}
                className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs transition-colors ${
                  device === 'mobile' ? 'bg-[#C9A961] font-semibold text-black' : 'text-white/60 hover:text-white'
                }`}
              >
                <Smartphone className="h-3.5 w-3.5" /> iPhone view
              </button>
              <button
                onClick={() => setDevice('desktop')}
                className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs transition-colors ${
                  device === 'desktop' ? 'bg-[#C9A961] font-semibold text-black' : 'text-white/60 hover:text-white'
                }`}
              >
                <Monitor className="h-3.5 w-3.5" /> Desktop view
              </button>
            </div>
            <div className="flex flex-1 items-center justify-center">
              {device === 'mobile' ? (
                <PhoneFrame src={slide.images.mobile} alt={slide.imageAlt} />
              ) : (
                <DesktopFrame src={slide.images.desktop} alt={slide.imageAlt} />
              )}
            </div>
            <p className="mt-4 text-center text-[10px] uppercase tracking-[0.25em] text-white/30">
              What you'll see in the Hub · {device === 'mobile' ? 'mobile' : 'desktop'}
            </p>
          </div>
        </div>
      ) : (
        /* ── Slide 16: Review ── */
        <div className="mt-6 rounded-3xl border border-[#C9A961]/25 bg-gradient-to-br from-[#C9A961]/[0.08] via-transparent to-[#1A3B5C]/20 p-6 sm:p-10">
          <div className="text-[11px] font-medium uppercase tracking-[0.35em] text-[#C9A961]">Course review · You made it</div>
          <h1 className="mt-3 text-2xl font-light tracking-tight text-white sm:text-4xl">
            Everything you just learned, <span className="text-[#E4C77F]">in one place.</span>
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/55">
            Fifteen slides covering all 21 Academy sections. Tap any recap to revisit a slide, then take the
            assessment to earn your certification.
          </p>

          <div className="mt-7 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {PRESENTATION_SLIDES.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setIdx(i)}
                className="group flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#C9A961]/40"
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/[0.04] text-xs font-semibold text-[#E4C77F]">
                  {s.num}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-xs font-medium text-white">{s.title}</div>
                  <div className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-white/45">{s.recap}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <div className="mb-3 text-xs font-medium uppercase tracking-[0.25em] text-[#C9A961]">The five big ideas</div>
            <div className="space-y-2.5">
              {REVIEW_BIG_IDEAS.map((b) => (
                <div key={b} className="flex items-start gap-2.5 text-sm text-white/70">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> {b}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={onStartQuiz}
              className="flex items-center gap-2 rounded-full bg-[#C9A961] px-8 py-3.5 text-sm font-semibold text-black shadow-lg shadow-[#C9A961]/25 transition-all hover:bg-[#E4C77F]"
            >
              <Award className="h-4 w-4" /> Take the learning assessment
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={downloadTrainingCopyPdf}
              className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm text-white/75 hover:border-[#C9A961]/60 hover:text-white"
            >
              <FileText className="h-4 w-4" /> Download training copy
            </button>
            <button
              onClick={downloadInvestorDeckPdf}
              className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm text-white/75 hover:border-[#C9A961]/60 hover:text-white"
            >
              <Presentation className="h-4 w-4" /> Download investor & client deck
            </button>
          </div>
        </div>
      )}

      {/* ── Prev / Next ── */}
      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={goPrev}
          disabled={idx === 0}
          className="flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm text-white/70 transition-colors hover:border-[#C9A961]/50 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
        >
          <ArrowLeft className="h-4 w-4" /> Previous
        </button>
        <div className="hidden gap-1.5 sm:flex">
          {Array.from({ length: PRESENTATION_STEPS }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2 rounded-full transition-all ${
                i === idx ? 'w-6 bg-[#C9A961]' : progress.visited[i === REVIEW_INDEX ? 'pres-review' : PRESENTATION_SLIDES[i].id] ? 'w-2 bg-[#C9A961]/40' : 'w-2 bg-white/15'
              }`}
            />
          ))}
        </div>
        {isReview ? (
          <button
            onClick={onStartQuiz}
            className="flex items-center gap-2 rounded-full bg-[#C9A961] px-6 py-3 text-sm font-semibold text-black hover:bg-[#E4C77F]"
          >
            <Award className="h-4 w-4" /> Assessment
          </button>
        ) : (
          <button
            onClick={goNext}
            className="flex items-center gap-2 rounded-full bg-[#C9A961] px-6 py-3 text-sm font-semibold text-black transition-all hover:bg-[#E4C77F]"
          >
            Next <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PresentationPlayer;
